import { useEffect, useState } from "react";
import {
  Search,
  ChevronDown,
  ArrowRight,
  ArrowUpRight,
  AlertTriangle,
  HeartHandshake,
  UserSearch,
  Briefcase,
  Globe,
} from "lucide-react";

import GovMenu from "../components/GovMenu";

const DURATION = 7000; // ms per slide

/* Authentic-themed imagery (agency homepages that use photography) */
const FTC_STATUE =
  "https://images.unsplash.com/photo-1756633231294-f72b004e8c5c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1500&auto=format&fit=crop";
const FBI_FLAG =
  "https://images.unsplash.com/photo-1633059050703-0f1b50828402?crop=entropy&cs=srgb&fm=jpg&q=80&w=1700&auto=format&fit=crop";
const IRS_LAPTOP =
  "https://images.unsplash.com/photo-1604933762021-54a5858c9832?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000&auto=format&fit=crop";
const SSA_COUPLE =
  "https://images.unsplash.com/photo-1554331292-735256644d5f?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200&auto=format&fit=crop";
const DOJ_BUILDING =
  "https://images.pexels.com/photos/14846501/pexels-photo-14846501.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1500";
const DHS_LIBERTY =
  "https://images.unsplash.com/photo-1762091409352-e595c9ecc9c9?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&auto=format&fit=crop";
const TREASURY_BUILDING =
  "https://images.pexels.com/photos/14955941/pexels-photo-14955941.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1500";
const STATE_FLAG =
  "https://images.unsplash.com/photo-1499200493734-6ba25a83f77c?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600&auto=format&fit=crop";

/* -------------------------------------------------------------------------- */
/*  Shared federal chrome                                                     */
/* -------------------------------------------------------------------------- */
const Flag = ({ className = "w-4 h-3" }) => (
  <svg viewBox="0 0 20 14" className={className} aria-hidden="true">
    <rect width="20" height="14" fill="#f7f7f7" />
    {[0, 1, 2, 3, 4, 5, 6].map((i) => (
      <rect key={i} y={i * 2} width="20" height="1" fill="#b22234" />
    ))}
    <rect width="9" height="7" fill="#3c3b6e" />
  </svg>
);

const GovBanner = () => (
  <div className="relative z-50 bg-[#f0f0f0] text-[#1b1b1b] w-full text-[11px] border-b border-gray-300">
    <div className="max-w-screen-xl mx-auto px-4 sm:px-8 py-1.5 space-y-0.5">
      <div className="flex items-center gap-2">
        <Flag />
        <span>An official website of the United States government</span>
        <span className="hidden sm:inline-flex items-center gap-1 font-semibold ml-1">
          Here's how you know <ChevronDown className="w-3 h-3" />
        </span>
        <div className="ml-auto">
          <GovMenu />
        </div>
      </div>
      <div className="flex items-start gap-2">
        <span className="w-4 shrink-0" aria-hidden="true" />
        <span>This site is not a public domain. It is an official agency server, only used by agency persons.</span>
      </div>
    </div>
  </div>
);

const Seal = ({ letters, ring = "#fff", fill = "transparent", text = "#fff", className = "w-12 h-12 sm:w-14 sm:h-14" }) => (
  <svg viewBox="0 0 64 64" className={`${className} shrink-0`} aria-hidden="true">
    <circle cx="32" cy="32" r="30" fill={fill} stroke={ring} strokeWidth="1.5" />
    <circle cx="32" cy="32" r="23.5" fill="none" stroke={ring} strokeWidth="0.7" opacity="0.6" />
    {Array.from({ length: 24 }).map((_, i) => {
      const a = (i / 24) * Math.PI * 2;
      return (
        <circle key={i} cx={32 + Math.cos(a) * 26.6} cy={32 + Math.sin(a) * 26.6} r="0.6" fill={ring} opacity="0.7" />
      );
    })}
    <text x="32" y="39" textAnchor="middle" fontFamily="'Source Serif 4', serif" fontWeight="700" fontSize="17" fill={text}>
      {letters}
    </text>
  </svg>
);

const SLIDE_W = 100 / 8; // 8 agencies
const slideClass = "shrink-0 h-full flex flex-col overflow-hidden";

/* -------------------------------------------------------------------------- */
/*  1 — Federal Trade Commission                                              */
/* -------------------------------------------------------------------------- */
const FTCSlide = () => (
  <section data-testid="agency-slide-ftc" className={`${slideClass} bg-white`} style={{ width: `${SLIDE_W}%` }}>
    <div className="relative text-white" style={{ backgroundColor: "#14477d" }}>
      <img src={FTC_STATUE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
      <div className="relative max-w-screen-xl mx-auto px-4 sm:px-8 py-4 flex items-center gap-4">
        <Seal letters="FTC" ring="#cfe0f0" text="#fff" />
        <div>
          <div className="font-serif font-bold text-lg sm:text-2xl leading-tight">FEDERAL TRADE COMMISSION</div>
          <div className="text-[10px] sm:text-xs tracking-[0.22em] text-sky-200 mt-0.5">PROTECTING AMERICA'S CONSUMERS</div>
        </div>
        <Search className="ml-auto w-5 h-5 text-sky-100" />
      </div>
    </div>
    <div className="text-white text-[12px] font-semibold" style={{ backgroundColor: "#0f3763" }}>
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 h-10 flex items-center gap-6 overflow-hidden whitespace-nowrap">
        {["I Would Like To…", "Enforcement", "Policy", "Advice and Guidance", "News and Events", "About the FTC"].map((n) => (
          <span key={n} className="hover:text-sky-200 cursor-default">{n}</span>
        ))}
      </div>
    </div>
    <div className="relative flex-1" style={{ backgroundColor: "#0e2f57" }}>
      <img src={FTC_STATUE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#0e2f57 42%, rgba(14,47,87,0.2))" }} />
      <div className="relative max-w-screen-xl mx-auto px-4 sm:px-8 h-full flex flex-col justify-center pb-28">
        <h1 className="text-white font-serif font-bold text-3xl sm:text-5xl max-w-xl leading-tight">
          Cybersecurity for Small Businesses
        </h1>
        <p className="mt-4 text-sky-100 max-w-lg text-sm sm:text-base leading-relaxed">
          October is Cybersecurity Awareness Month. Find basic information you need to protect your business from cyberattacks.
        </p>
        <button className="mt-6 w-fit bg-white text-[#14477d] font-semibold text-sm px-6 h-11 rounded-sm hover:bg-sky-50 transition-colors">
          Learn More
        </button>
      </div>
      <div className="absolute bottom-0 inset-x-0 bg-white border-t-4" style={{ borderColor: "#c8102e" }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-8 py-4">
          <div className="text-[11px] font-bold tracking-[0.18em] uppercase" style={{ color: "#14477d" }}>Take Action</div>
          <div className="mt-2 flex flex-wrap gap-x-7 gap-y-1.5 text-sm font-medium" style={{ color: "#14477d" }}>
            {["Report fraud", "Get your free credit report", "Report identity theft", "Register for Do Not Call"].map((n) => (
              <span key={n} className="inline-flex items-center gap-1 border-b-2 border-transparent hover:border-[#c8102e] cursor-default">
                {n}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  2 — Federal Bureau of Investigation                                       */
/* -------------------------------------------------------------------------- */
const FBI_STATS = [
  { v: "↓20%", l: "Drop in nationwide murder rate" },
  { v: "53,969+", l: "Violent crime arrests" },
  { v: "3,258+", l: "Kilos of fentanyl seized" },
  { v: "45,500+", l: "Arrests — and counting" },
];
const FBISlide = () => (
  <section data-testid="agency-slide-fbi" className={slideClass} style={{ width: `${SLIDE_W}%`, backgroundColor: "#0c1116" }}>
    <div className="border-b border-white/10">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-4 text-white">
        <Seal letters="FBI" ring="#c0a24a" text="#c0a24a" className="w-11 h-11" />
        <div className="leading-none">
          <div className="font-serif font-bold text-lg tracking-wide">FBI</div>
          <div className="text-[9px] tracking-[0.22em] text-gray-400 mt-1">FEDERAL BUREAU OF INVESTIGATION</div>
        </div>
        <nav className="ml-8 hidden lg:flex items-center gap-6 text-[12px] text-gray-200">
          {["Home", "News", "What We Investigate", "Services", "About"].map((n) => (
            <span key={n} className="hover:text-[#c0a24a] cursor-default">{n}</span>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-4">
          <Search className="w-5 h-5 text-gray-300" />
          <button className="bg-[#0a2a4f] text-white text-[12px] font-semibold px-4 h-9 rounded-sm hover:bg-[#0d366a] transition-colors">
            Submit a Tip
          </button>
        </div>
      </div>
    </div>
    <div className="relative flex-1">
      <img src={FBI_FLAG} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0c1116] via-[#0c1116]/80 to-[#0c1116]/40" />
      <div className="relative h-full max-w-screen-xl mx-auto px-4 sm:px-8 flex flex-col justify-center py-8">
        <span className="text-[#c0a24a] text-[11px] font-semibold tracking-[0.28em] uppercase">Results</span>
        <h1 className="mt-3 text-white font-serif font-bold text-3xl sm:text-5xl max-w-2xl leading-[1.08]">
          We're delivering results for the American people.
        </h1>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 max-w-3xl">
          {FBI_STATS.map((s) => (
            <div key={s.l} className="bg-white/5 border border-white/10 backdrop-blur-sm p-4">
              <div className="font-serif font-bold text-2xl sm:text-3xl text-[#c0a24a]">{s.v}</div>
              <div className="mt-1 text-[11px] leading-snug text-gray-300">{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  3 — Internal Revenue Service                                              */
/* -------------------------------------------------------------------------- */
const IRSSlide = () => (
  <section data-testid="agency-slide-irs" className={`${slideClass} bg-white`} style={{ width: `${SLIDE_W}%` }}>
    <div className="border-b border-gray-200 bg-white">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-4">
        <Seal letters="IRS" ring="#0a2f5c" fill="#0a2f5c" text="#fff" className="w-11 h-11" />
        <div className="leading-none">
          <div className="font-serif font-bold text-xl tracking-tight" style={{ color: "#0a2f5c" }}>IRS</div>
          <div className="text-[9px] tracking-[0.18em] text-gray-500 mt-1">INTERNAL REVENUE SERVICE</div>
        </div>
        <nav className="ml-8 hidden lg:flex items-center gap-6 text-[13px] text-gray-700">
          {["File", "Pay", "Refunds", "Credits & Deductions", "Forms & Instructions"].map((n) => (
            <span key={n} className="hover:text-[#0071bc] cursor-default">{n}</span>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-4">
          <Search className="w-5 h-5 text-gray-500" />
          <button className="text-[13px] font-semibold" style={{ color: "#0071bc" }}>Sign in to your account</button>
        </div>
      </div>
    </div>
    <div className="text-white" style={{ backgroundColor: "#0a2f5c" }}>
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 py-8 grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <h1 className="font-serif font-bold text-3xl sm:text-4xl leading-tight">Do more with an IRS account</h1>
          <p className="mt-3 text-sky-100 text-sm sm:text-base max-w-md">
            Access your tax information with an Individual, Business or Tax Pro Account.
          </p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-sky-100">
            {["Refund status", "Tax records", "Payments", "Notifications"].map((n) => (
              <span key={n} className="inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-sky-300" /> {n}
              </span>
            ))}
          </div>
          <button className="mt-6 bg-white text-[#0a2f5c] font-semibold text-sm px-6 h-11 rounded-sm hover:bg-sky-50 transition-colors">
            Create account
          </button>
        </div>
        <div className="hidden lg:block">
          <img src={IRS_LAPTOP} alt="" className="w-full h-44 object-cover rounded-md shadow-lg" />
        </div>
      </div>
    </div>
    <div className="flex-1 flex flex-col justify-center" style={{ backgroundColor: "#f1f3f6" }}>
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 py-8 w-full">
        <h2 className="font-serif font-bold text-2xl sm:text-3xl" style={{ color: "#0a2f5c" }}>What do you need today?</h2>
        <div className="mt-4 flex items-center bg-white border-2 rounded-sm max-w-2xl h-14 px-4" style={{ borderColor: "#0a2f5c" }}>
          <Search className="w-5 h-5 text-gray-400" />
          <span className="ml-3 text-gray-400 text-sm">Search IRS tasks, forms, and answers</span>
          <button className="ml-auto text-white font-semibold text-sm px-5 h-10 rounded-sm" style={{ backgroundColor: "#0a2f5c" }}>Search</button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-gray-500 font-semibold">Popular:</span>
          {["Make a payment", "Get a transcript", "Form 1040", "Payment plan"].map((n) => (
            <span key={n} className="px-3 py-1 bg-white border border-gray-300 rounded-full text-[#0071bc] cursor-default hover:border-[#0071bc]">
              {n}
            </span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  4 — Department of Justice                                                 */
/* -------------------------------------------------------------------------- */
const DOJ_ACTIONS = [
  { icon: AlertTriangle, l: "Report a Crime" },
  { icon: HeartHandshake, l: "Help for Crime Victims" },
  { icon: UserSearch, l: "Wanted Fugitives" },
  { icon: Briefcase, l: "Careers at DOJ" },
];
const DOJSlide = () => (
  <section data-testid="agency-slide-doj" className={`${slideClass} bg-white`} style={{ width: `${SLIDE_W}%` }}>
    <div className="border-b border-gray-200 bg-white">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-4">
        <Seal letters="DOJ" ring="#1b4b8f" fill="#162e51" text="#d4af37" className="w-11 h-11" />
        <div className="font-serif font-bold text-sm sm:text-base tracking-wide" style={{ color: "#162e51" }}>
          THE UNITED STATES DEPARTMENT OF JUSTICE
        </div>
        <nav className="ml-8 hidden lg:flex items-center gap-6 text-[13px] text-gray-700">
          {["About", "Our Work", "News", "Resources", "Careers", "Contact"].map((n) => (
            <span key={n} className="hover:text-[#1b4b8f] cursor-default">{n}</span>
          ))}
        </nav>
        <Search className="ml-auto w-5 h-5 text-gray-500" />
      </div>
    </div>
    <div className="relative flex-1 flex flex-col">
      <div className="relative flex-1">
        <img src={DOJ_BUILDING} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#0d1d38 55%, rgba(13,29,56,0.45))" }} />
        <div className="relative h-full max-w-screen-xl mx-auto px-4 sm:px-8 flex flex-col justify-center py-8">
          <div className="flex items-center gap-3">
            <Seal letters="DOJ" ring="#d4af37" text="#d4af37" className="w-10 h-10" />
            <span className="text-[#d4af37] text-[11px] font-semibold tracking-[0.28em] uppercase">Our Mission</span>
          </div>
          <h1 className="mt-4 text-white font-serif font-bold text-2xl sm:text-4xl max-w-3xl leading-[1.12]">
            To uphold the rule of law, to keep our country safe, and to protect civil rights.
          </h1>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-2 text-sm text-gray-200">
            {[["6", "Litigating Divisions"], ["5", "Law Enforcement Bureaus"], ["94", "U.S. Attorneys' Offices"], ["$3.2B+", "Grants to Partners"]].map(([v, l]) => (
              <span key={l}>
                <span className="font-serif font-bold text-[#d4af37] text-lg mr-1.5">{v}</span>
                {l}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div style={{ backgroundColor: "#162e51" }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-8 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {DOJ_ACTIONS.map((a) => (
            <div key={a.l} className="flex items-center gap-3 text-white">
              <a.icon className="w-5 h-5 text-[#d4af37] shrink-0" strokeWidth={1.6} />
              <span className="text-[13px] font-medium leading-tight">{a.l}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  5 — Social Security Administration                                        */
/* -------------------------------------------------------------------------- */
const SSA_CARDS = ["Get a benefits estimate", "Check eligibility", "Check your status", "Replace your card"];
const SSASlide = () => (
  <section data-testid="agency-slide-ssa" className={`${slideClass} bg-white`} style={{ width: `${SLIDE_W}%` }}>
    <div className="border-b border-gray-200 bg-white">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-4">
        <Seal letters="SSA" ring="#0b4778" fill="#0b4778" text="#fff" className="w-11 h-11" />
        <div className="leading-none">
          <div className="font-serif font-bold text-lg" style={{ color: "#0b4778" }}>Social Security</div>
          <div className="text-[9px] tracking-[0.16em] text-gray-500 mt-1">SECURING TODAY AND TOMORROW</div>
        </div>
        <nav className="ml-8 hidden lg:flex items-center gap-6 text-[13px] text-gray-700">
          {["Benefits", "Medicare", "Card & record", "Scams"].map((n) => (
            <span key={n} className="hover:text-[#0071bc] cursor-default">{n}</span>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-4">
          <span className="hidden sm:inline-flex items-center gap-1 text-[13px] text-gray-600"><Globe className="w-4 h-4" /> Español</span>
          <button className="text-white text-[13px] font-semibold px-4 h-9 rounded-sm" style={{ backgroundColor: "#0b4778" }}>Sign in</button>
        </div>
      </div>
    </div>
    <div style={{ backgroundColor: "#e8f1f8" }}>
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 py-8 grid lg:grid-cols-2 gap-8 items-center">
        <div>
          <h1 className="font-serif font-bold text-3xl sm:text-5xl leading-tight" style={{ color: "#0b4778" }}>
            Your most-needed services, online
          </h1>
          <p className="mt-4 text-gray-700 text-sm sm:text-base max-w-md">
            With a secure <em>my</em> Social Security account, you can get services and manage your benefits — anywhere, anytime.
          </p>
          <button className="mt-6 inline-flex items-center gap-2 text-white font-semibold text-sm px-6 h-11 rounded-sm" style={{ backgroundColor: "#0071bc" }}>
            Sign in or create an account <ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <div className="hidden lg:block">
          <img src={SSA_COUPLE} alt="" className="w-full h-44 object-cover rounded-md shadow" />
        </div>
      </div>
    </div>
    <div className="flex-1 flex flex-col justify-center bg-white">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 py-8 w-full grid grid-cols-2 lg:grid-cols-4 gap-4">
        {SSA_CARDS.map((c) => (
          <div key={c} className="border border-gray-200 p-5 hover:border-[#0071bc] hover:shadow-md transition-all cursor-default">
            <div className="font-serif font-semibold text-base" style={{ color: "#0b4778" }}>{c}</div>
            <ArrowUpRight className="mt-6 w-5 h-5" style={{ color: "#0071bc" }} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  6 — Department of Homeland Security                                        */
/* -------------------------------------------------------------------------- */
const DHS_COMPONENTS = ["FEMA", "CBP", "TSA", "ICE", "USCIS", "U.S. Coast Guard", "Secret Service", "CISA"];
const DHSSlide = () => (
  <section data-testid="agency-slide-dhs" className={slideClass} style={{ width: `${SLIDE_W}%`, backgroundColor: "#1b2a4a" }}>
    <div className="border-b border-white/10">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-4 text-white">
        <Seal letters="DHS" ring="#c8c8d8" text="#fff" className="w-11 h-11" />
        <div className="leading-none">
          <div className="font-serif font-bold text-lg">Homeland Security</div>
          <div className="text-[9px] tracking-[0.2em] text-slate-300 mt-1">U.S. DEPARTMENT OF HOMELAND SECURITY</div>
        </div>
        <nav className="ml-8 hidden lg:flex items-center gap-6 text-[12px] text-slate-200">
          {["Topics", "How Do I", "Get Involved", "News", "About DHS"].map((n) => (
            <span key={n} className="hover:text-white cursor-default">{n}</span>
          ))}
        </nav>
        <Search className="ml-auto w-5 h-5 text-slate-300" />
      </div>
    </div>
    <div className="relative flex-1 flex flex-col">
      <div className="relative flex-1">
        <img src={DHS_LIBERTY} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#1b2a4a 52%, rgba(27,42,74,0.35))" }} />
        <div className="relative h-full max-w-screen-xl mx-auto px-4 sm:px-8 flex flex-col justify-center py-8">
          <span className="text-[11px] font-semibold tracking-[0.26em] uppercase" style={{ color: "#e5a84b" }}>With Honor and Integrity</span>
          <h1 className="mt-3 text-white font-serif font-bold text-3xl sm:text-5xl max-w-2xl leading-[1.08]">
            Safeguarding the American people, our homeland, and our values.
          </h1>
          <p className="mt-4 text-slate-200 max-w-lg text-sm sm:text-base leading-relaxed">
            A unified national effort to secure the United States — from border and transportation security to cyber defense and disaster response.
          </p>
        </div>
      </div>
      <div style={{ backgroundColor: "#13203b" }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-8 py-4">
          <div className="text-[11px] font-bold tracking-[0.18em] uppercase mb-2" style={{ color: "#e5a84b" }}>Operational Components</div>
          <div className="flex flex-wrap gap-2">
            {DHS_COMPONENTS.map((c) => (
              <span key={c} className="px-3 py-1 text-[12px] font-medium text-white border border-white/20 rounded-sm cursor-default hover:border-[#e5a84b]">
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  7 — Department of the Treasury                                             */
/* -------------------------------------------------------------------------- */
const TREASURY_RATES = [
  ["1 Mo", "4.12"], ["3 Mo", "4.26"], ["1 Yr", "4.46"], ["2 Yr", "4.74"], ["10 Yr", "5.09"], ["30 Yr", "5.43"],
];
const TreasurySlide = () => (
  <section data-testid="agency-slide-treasury" className={`${slideClass} bg-white`} style={{ width: `${SLIDE_W}%` }}>
    <div className="border-b border-gray-200 bg-white">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-4">
        <Seal letters="T" ring="#1a5c43" fill="#1a5c43" text="#fff" className="w-11 h-11" />
        <div className="font-serif font-bold text-base sm:text-lg tracking-tight" style={{ color: "#1a5c43" }}>
          U.S. Department of the Treasury
        </div>
        <nav className="ml-8 hidden lg:flex items-center gap-6 text-[13px] text-gray-700">
          {["About", "Policy Issues", "Data", "Services", "News"].map((n) => (
            <span key={n} className="hover:text-[#1a5c43] cursor-default">{n}</span>
          ))}
        </nav>
        <Search className="ml-auto w-5 h-5 text-gray-500" />
      </div>
    </div>
    <div className="relative flex-1">
      <img src={TREASURY_BUILDING} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#0f3e2e 50%, rgba(15,62,46,0.4))" }} />
      <div className="relative h-full max-w-screen-xl mx-auto px-4 sm:px-8 flex flex-col justify-center py-8">
        <span className="text-[11px] font-semibold tracking-[0.26em] uppercase" style={{ color: "#d9b85c" }}>Est. 1789</span>
        <h1 className="mt-3 text-white font-serif font-bold text-3xl sm:text-5xl max-w-2xl leading-[1.08]">
          Managing the nation's finances.
        </h1>
        <p className="mt-4 text-emerald-50/90 max-w-lg text-sm sm:text-base leading-relaxed">
          The Treasury collects taxes and duties, manages federal finances, and protects the integrity of the nation's financial systems.
        </p>
        <div className="mt-6 bg-white/95 w-fit max-w-full px-5 py-4 rounded-sm">
          <div className="text-[11px] font-bold tracking-[0.14em] uppercase" style={{ color: "#1a5c43" }}>Daily Treasury Par Yield Curve · 10/16/2026</div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
            {TREASURY_RATES.map(([k, v]) => (
              <div key={k} className="text-center">
                <div className="font-serif font-bold text-lg" style={{ color: "#1a5c43" }}>{v}</div>
                <div className="font-mono text-[10px] text-gray-500">{k}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  8 — Department of State                                                    */
/* -------------------------------------------------------------------------- */
const STATE_CARDS = ["Get a U.S. Passport", "Travel Advisories", "Countries & Areas", "Careers in Diplomacy"];
const StateSlide = () => (
  <section data-testid="agency-slide-state" className={`${slideClass}`} style={{ width: `${SLIDE_W}%`, backgroundColor: "#13233f" }}>
    <div className="border-b border-white/10">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-8 h-16 flex items-center gap-4 text-white">
        <Seal letters="DOS" ring="#c9a24a" text="#c9a24a" className="w-11 h-11" />
        <div className="leading-none">
          <div className="font-serif font-bold text-base sm:text-lg tracking-wide">U.S. DEPARTMENT OF STATE</div>
          <div className="text-[9px] tracking-[0.2em] text-slate-300 mt-1">DIPLOMACY IN ACTION</div>
        </div>
        <nav className="ml-8 hidden lg:flex items-center gap-6 text-[12px] text-slate-200">
          {["Policy Issues", "Countries & Areas", "Bureaus & Offices", "Travel", "Careers"].map((n) => (
            <span key={n} className="hover:text-[#c9a24a] cursor-default">{n}</span>
          ))}
        </nav>
        <Search className="ml-auto w-5 h-5 text-slate-300" />
      </div>
    </div>
    <div className="relative flex-1">
      <img src={STATE_FLAG} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#13233f 50%, rgba(19,35,63,0.35))" }} />
      <div className="relative h-full max-w-screen-xl mx-auto px-4 sm:px-8 flex flex-col justify-center py-8">
        <span className="text-[11px] font-semibold tracking-[0.26em] uppercase" style={{ color: "#c9a24a" }}>250 Years of American Diplomacy</span>
        <h1 className="mt-3 text-white font-serif font-bold text-3xl sm:text-5xl max-w-3xl leading-[1.1]">
          Leading America's foreign policy to advance the interests and security of the American people.
        </h1>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl">
          {STATE_CARDS.map((c) => (
            <div key={c} className="bg-white/5 border border-white/15 p-4 backdrop-blur-sm cursor-default hover:border-[#c9a24a] transition-colors">
              <div className="text-sm font-medium text-white leading-snug">{c}</div>
              <ArrowUpRight className="mt-3 w-4 h-4 text-[#c9a24a]" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------------------------------------------------- */
/*  Auto-advancing horizontal slideshow                                       */
/* -------------------------------------------------------------------------- */
const SLIDES = [FTCSlide, FBISlide, IRSSlide, DOJSlide, SSASlide, DHSSlide, TreasurySlide, StateSlide];

const Home = () => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), DURATION);
    return () => clearInterval(id);
  }, []);

  return (
    <div data-testid="home-page" className="relative w-full overflow-hidden flex flex-col" style={{ height: "100vh" }}>
      <GovBanner />
      <div data-testid="agency-slideshow" className="relative w-full flex-1 overflow-hidden">
        <div
          className="flex h-full"
          style={{
            width: `${SLIDES.length * 100}%`,
            transform: `translateX(-${index * SLIDE_W}%)`,
            transition: "transform 1000ms cubic-bezier(0.76,0,0.24,1)",
          }}
        >
          {SLIDES.map((S, i) => (
            <S key={i} />
          ))}
        </div>

        {/* auto-advance progress bar */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-black/10 z-20">
          <div
            key={index}
            className="h-full bg-[#c8102e]"
            style={{ animation: `anca-progress ${DURATION}ms linear forwards` }}
          />
        </div>
      </div>

      <style>{`@keyframes anca-progress { from { width: 0% } to { width: 100% } }`}</style>
    </div>
  );
};

export default Home;
